import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactFlow, {
  Background, Controls, MiniMap,
  addEdge as rfAddEdge, applyNodeChanges, applyEdgeChanges,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { fetchScenario, updateScenario } from '../slices/scenarioSlice';
import { loadFlow, setNodes, setEdges, selectNode, addEdge as flowAddEdge } from '../slices/flowSlice';
import { fetchExecutions } from '../slices/executionSlice';

import FacebookTriggerNode from '../components/nodes/FacebookTriggerNode';
import HttpRequestNode from '../components/nodes/HttpRequestNode';
import GoogleSheetsNode from '../components/nodes/GoogleSheetsNode';
import NodeConfigPanel from '../components/NodeConfigPanel';
import { HiOutlineSave, HiOutlinePlay, HiOutlineArrowLeft, HiOutlinePlus, HiOutlineClock, HiOutlineCheckCircle, HiOutlineXCircle } from 'react-icons/hi';

const nodeTypes = {
  facebookTrigger: FacebookTriggerNode,
  httpRequest: HttpRequestNode,
  googleSheets: GoogleSheetsNode,
};

const NODE_TEMPLATES = [
  { type: 'facebookTrigger', label: 'Facebook Lead Trigger', color: '#1877F2' },
  { type: 'httpRequest', label: 'HTTP Request', color: '#F59E0B' },
  { type: 'googleSheets', label: 'Google Sheets', color: '#34A853' },
];

export default function ScenarioBuilder() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { current } = useSelector((s) => s.scenarios);
  const { nodes, edges, selectedNode } = useSelector((s) => s.flow);
  const latestExecution = useSelector((s) => s.executions.list?.[0]);
  const [saving, setSaving] = useState(false);
  const [showNodePicker, setShowNodePicker] = useState(false);
  const counterRef = useRef(0);

  useEffect(() => {
    dispatch(fetchScenario(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (current) {
      dispatch(loadFlow({ nodes: current.nodes, edges: current.edges }));
      const maxId = (current.nodes || []).reduce((max, n) => {
        const num = parseInt(n.id?.replace(/\D/g, '')) || 0;
        return Math.max(max, num);
      }, 0);
      counterRef.current = maxId;
    }
  }, [current, dispatch]);

  useEffect(() => {
    if (!id) return undefined;

    dispatch(fetchExecutions(id));
    const intervalId = window.setInterval(() => {
      dispatch(fetchExecutions(id));
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [dispatch, id]);

  const onNodesChange = useCallback(
    (changes) => {
      dispatch(setNodes(applyNodeChanges(changes, nodes)));
    },
    [nodes, dispatch]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      dispatch(setEdges(applyEdgeChanges(changes, edges)));
    },
    [edges, dispatch]
  );

  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        id: `e-${params.source}-${params.target}`,
        animated: true,
        style: { stroke: '#6366f1' },
      };
      dispatch(setEdges(rfAddEdge(newEdge, edges)));
    },
    [edges, dispatch]
  );

  const onNodeClick = useCallback(
    (_e, node) => {
      dispatch(selectNode(node));
    },
    [dispatch]
  );

  const onPaneClick = useCallback(() => {
    dispatch(selectNode(null));
  }, [dispatch]);

  const addNode = (type) => {
    counterRef.current += 1;
    const nodeId = `node-${counterRef.current}`;
    const template = NODE_TEMPLATES.find((t) => t.type === type);
    const newNode = {
      id: nodeId,
      type,
      position: { x: 250 + Math.random() * 200, y: 100 + counterRef.current * 120 },
      data: { label: template?.label || type, config: {}, type },
    };
    dispatch(setNodes([...nodes, newNode]));
    setShowNodePicker(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await dispatch(updateScenario({ id, data: { nodes, edges } }));
    setSaving(false);
  };

  const handleTestRun = async () => {
    try {
      await handleSave();
      const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
      await fetch(`${baseUrl}/webhook/test/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Test User', email: 'test@example.com', phone: '+1234567890' }),
      });
      alert('Test execution queued! Check execution logs.');
    } catch (err) {
      alert('Test run failed: ' + err.message);
    }
  };

  const latestStatusIcon = latestExecution?.status === 'success'
    ? <HiOutlineCheckCircle className="text-emerald-500" size={16} />
    : latestExecution?.status === 'error'
      ? <HiOutlineXCircle className="text-red-500" size={16} />
      : <HiOutlineClock className="text-amber-500" size={16} />;

  if (!current) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in -m-6 flex flex-col h-[calc(100vh-4rem)] lg:h-screen">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/scenarios')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
            <HiOutlineArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-semibold truncate max-w-xs">{current.name}</h1>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                current.status === 'active'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
              }`}>
                {current.status}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
              {latestStatusIcon}
              {latestExecution
                ? `Last run: ${latestExecution.status} at ${new Date(latestExecution.createdAt).toLocaleTimeString()}`
                : 'No executions yet. Live status refreshes every 10s.'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowNodePicker(!showNodePicker)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            >
              <HiOutlinePlus size={16} /> Add Node
            </button>
            {showNodePicker && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 py-2 z-50">
                {NODE_TEMPLATES.map((t) => (
                  <button
                    key={t.type}
                    onClick={() => addNode(t.type)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                  >
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={handleTestRun} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition">
            <HiOutlinePlay size={16} /> Test Run
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-brand-600 hover:bg-brand-700 text-white font-medium transition disabled:opacity-50">
            <HiOutlineSave size={16} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 flex">
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            className="bg-gray-50 dark:bg-gray-950"
          >
            <Background color="#e5e7eb" gap={20} />
            <Controls className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" />
            <MiniMap className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800" />
          </ReactFlow>
        </div>

        {/* Side Panel */}
        {selectedNode && (
          <NodeConfigPanel />
        )}
      </div>
    </div>
  );
}
